import { Entity } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';

/**
 * Base entity for all pending tasks stored in examState.pendingTasks.
 * Each subtype registers itself with a desc builder via registerPendingTaskDescBuilder.
 *
 * Priorities:
 *   workflow — triggers an automatic generateAnswer restart; shown first in context
 *   high     — shown before normal; handled during normal conversation
 *   normal   — default
 */
@Entity()
export class PendingTask extends AdornisEntity {
  static override _class = 'PendingTask';
}

export type PendingTaskPriority = 'workflow' | 'high' | 'normal';

type PendingTaskDescBuilder = (task: PendingTask) => string;

const pendingTaskDescBuilders = new Map<string, PendingTaskDescBuilder>();

export function registerPendingTaskDescBuilder(taskClass: string, builder: PendingTaskDescBuilder): void {
  pendingTaskDescBuilders.set(taskClass, builder);
}

const PRIORITY_ORDER: Record<PendingTaskPriority, number> = { workflow: 0, high: 1, normal: 2 };

/**
 * Whether a task may be shown to / acted on by the agent. A task is ready unless it explicitly sets
 * `isReadyForAgent:false` — the opt-in hold-back used while a multi-step task (e.g. SetupStep) is
 * still accumulating results. Tasks without the field (grading, metadata, …) are always ready.
 */
export function isPendingTaskReady(task: Record<string, unknown> | PendingTask): boolean {
  return (task as Record<string, unknown>)?.isReadyForAgent !== false;
}

/**
 * Orchestrates all pending task desc builders into a single context string for the system prompt.
 * Each desc is prefixed with its priority so the agent can factor urgency into its decisions.
 * Workflow tasks appear first, then high, then normal.
 */
export function getPendingTaskDescs(pendingTasks: Array<Record<string, unknown>>): string {
  if (!Array.isArray(pendingTasks) || pendingTasks.length === 0) return '';

  // Held-back tasks (isReadyForAgent:false) stay hidden from the agent until they release.
  const visible = pendingTasks.filter(isPendingTaskReady);
  if (visible.length === 0) return '';

  const sorted = [...visible].sort((a, b) => {
    const pa = PRIORITY_ORDER[(a?.priority as PendingTaskPriority) ?? 'normal'] ?? 2;
    const pb = PRIORITY_ORDER[(b?.priority as PendingTaskPriority) ?? 'normal'] ?? 2;
    return pa - pb;
  });

  const descs = sorted
    .map(task => {
      const taskClass = String(task?._class ?? '');
      const builder = pendingTaskDescBuilders.get(taskClass);
      if (!builder) return '';
      const priority = (task?.priority as PendingTaskPriority) ?? 'normal';
      return `[Priorität: ${priority}]\n${builder(task as unknown as PendingTask)}`;
    })
    .filter(Boolean);

  if (descs.length === 0) return '';

  return `## AUSSTEHENDE HINTERGRUNDAUFGABEN (PENDING TASKS)
Diese Aufgaben wurden automatisch vom System erkannt und warten auf Bearbeitung.
Workflow Aufgaben müssen sofort erledigt werden, damit die Klausur weiter bearbeitet werden kann.
Hohe Priorität bedeutet, dass die Aufgabe beiläufig in die laufende Bearbeitung einbezogen werden sollte (z.B. als zusätzliches Tool in einer Toolnutzungsphase oder als Ergänzung zur Antwort je nach Aufgabe), vernachlässige dabei aber nie das aktuelle anliegen des Nutzers.
Normale Priorität bedeutet, dass die Aufgabe bearbeitet werden sollte, aber sie hat keine Dringlichkeit.
Teile dem Nutzer generell nichts über Hintergrundaufgaben mit, tu so als ob du das einfach so machst, weil du weißt, dass es getan werden muss, damit die Klausur bearbeitet werden kann.

${descs.join('\n\n')}`;
}

/** Returns true if any pending task has workflow priority and is ready (not held back). */
export function hasWorkflowPendingTasks(pendingTasks: Array<Record<string, unknown>>): boolean {
  return (
    Array.isArray(pendingTasks) &&
    pendingTasks.some(t => (t?.priority as PendingTaskPriority) === 'workflow' && isPendingTaskReady(t))
  );
}
